// Sidebar.jsx
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaCog,
  FaBriefcase,
  FaFolderOpen,
  FaChartBar,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaUser,
  FaBookOpen,
  FaUsers,
} from "react-icons/fa";
import { HiOutlineDocumentAdd } from "react-icons/hi";

const Sidebar = () => {
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();

  // Get user role from localStorage
  const getUserRole = () => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        return JSON.parse(userData).role;
      } catch (e) {
        return null;
      }
    }
    return localStorage.getItem('userRole');
  };

  const userRole = getUserRole();
  const isStaff = userRole === 'staff' || userRole === 'Staff';

  const handleLogout = () => {
    localStorage.clear(); // Clear all auth data
    navigate('/login', { replace: true });
  };

  const sidebarStyle = {
    width: "280px",
    backgroundColor: "#fff",
    height: "calc(100vh - 60px)",
    padding: "25px 0",
    boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: "60px",
    overflowY: "auto",
  };

  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    fontSize: "16px",
    color: "#333",
    textDecoration: "none",
    marginBottom: "6px",
    transition: "all 0.3s ease",
    cursor: "pointer",
    width: "100%",
    position: "relative",
    overflow: "hidden",
  };

  const hoverStyle = {
    background: "linear-gradient(120deg, rgba(0,74,173,0.18), rgba(173,216,255,0.35))",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    color: "#003c8f",
  };

  const activeStyle = {
    backgroundColor: "#004aad",
    color: "#ffffff",
    fontWeight: "bold",
  };

  const bottomBarStyle = {
    position: "absolute",
    bottom: 0,
    left: 0,
    height: "1px",
    width: "100%",
    backgroundColor: "#004aad",
    transition: "transform 0.35s ease",
    transformOrigin: "left",
  };

  const iconStyle = {
    marginRight: "12px",
    fontSize: "18px",
    zIndex: 2,
  };

  // Admin menu items
  const adminMenuItems = [
    { to: "/users", icon: <FaUsers style={iconStyle} />, label: "Users" },
    { to: "/accounts", icon: <FaBriefcase style={iconStyle} />, label: "Accounts" },
    { to: "/accountdetails", icon: <HiOutlineDocumentAdd style={iconStyle} />, label: "New Case" },
    { to: "/cases-opened", icon: <FaFolderOpen style={iconStyle} />, label: "By Month" },
    { to: "/statements", icon: <FaFileInvoiceDollar style={iconStyle} />, label: "Statements" },
    { to: "/postings", icon: <FaMoneyCheckAlt style={iconStyle} />, label: "Postings" },
    { to: "/case-setting", icon: <FaCog style={iconStyle} />, label: "Case Settings" },
    { to: "/manual-editor", icon: <FaBookOpen style={iconStyle} />, label: "Manual Editor" },
  ];

  // Staff menu items - Only Manual Editor
  const staffMenuItems = [
    { to: "/manual-editor", icon: <FaBookOpen style={iconStyle} />, label: "Manual Editor" },
  ];

  // Choose menu items based on role
  const menuItems = isStaff ? staffMenuItems : adminMenuItems;

  return (
    <div style={sidebarStyle}>
      {/* Role indicator */}
      <div style={{
        padding: '0 24px 15px',
        marginBottom: '10px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: isStaff ? '#fef3c7' : '#dbeafe',
          color: isStaff ? '#92400e' : '#1e40af',
        }}>
          {isStaff ? 'Staff Access' : 'Admin Access'}
        </div>
      </div>

      {menuItems.map((item, index) => (
        <NavLink
          key={index}
          to={item.to}
          style={({ isActive }) => ({
            ...menuItemStyle,
            ...(isActive ? activeStyle : {}),
            ...(hovered === index && !isActive ? hoverStyle : {}),
          })}
          onMouseEnter={() => setHovered(index)}
          onMouseLeave={() => setHovered(null)}
        >
          {({ isActive }) => (
            <>
              <div
                style={{
                  ...bottomBarStyle,
                  transform:
                    hovered === index || isActive
                      ? "scaleX(1)"
                      : "scaleX(0)",
                }}
              />
              {item.icon}
              <span style={{ zIndex: 2 }}>{item.label}</span>
            </>
          )}
        </NavLink>
      ))}

      {/* LOGOUT */}
      <div
        onClick={handleLogout}
        onMouseEnter={() => setHovered("logout")}
        onMouseLeave={() => setHovered(null)}
        style={{
          ...menuItemStyle,
          marginTop: "auto",
          justifyContent: "center",
          padding: "14px 24px",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "45px",
            height: "45px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ddd",
            border: hovered === "logout" ? "2px solid #ff0000" : "1px solid #004aad",
            borderRadius: "6px",
            transition: "all 0.3s ease",
          }}
        >
          <FaSignOutAlt
            style={{
              fontSize: "20px",
              color: "#333",
              zIndex: 2,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;